/**
 * Étape 6 — Résultat enrichi de l'estimation des aides.
 *
 * Affiche :
 *  - Montant total aides cumulées + taux de couverture
 *  - Détail par dispositif (MPR, CEE dont Coup de Pouce, Éco-PTZ, TVA 5,5)
 *  - Reste à charge estimé
 *  - Progression DPE visuelle (si dpeActuel renseigné)
 *  - Économie annuelle + retour sur investissement (si facture renseignée)
 *  - Warnings profil + prochaines étapes
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BadgeCheck,
  CheckCircle2,
  Euro,
  HandCoins,
  HelpCircle,
  Home,
  Phone,
  Receipt,
  RefreshCw,
  Sparkles,
  Timer,
  TrendingUp,
  Wallet,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { useAidsStore } from './useAidsStore';
import { computeAids, fmtEuro } from './compute';
import {
  findTravaux,
  TAG_TONE_CLASSES,
  TRAVAUX_TAG_META,
  type TravauxTag,
} from './travaux-config';

/* ─── Couleurs DPE ──────────────────────────────────────── */

const DPE_COLORS: Record<string, string> = {
  G: 'bg-red-600 text-white',
  F: 'bg-orange-500 text-white',
  E: 'bg-amber-400 text-stone-900',
  D: 'bg-yellow-300 text-stone-900',
  C: 'bg-lime-400 text-stone-900',
  B: 'bg-green-500 text-white',
  A: 'bg-emerald-600 text-white',
};

const DPE_BORDER: Record<string, string> = {
  G: 'border-red-300',
  F: 'border-orange-300',
  E: 'border-amber-300',
  D: 'border-yellow-300',
  C: 'border-lime-300',
  B: 'border-green-300',
  A: 'border-emerald-300',
};

/* ─── Composant principal ───────────────────────────────── */

export function Step6Resultat() {
  const profile = useAidsStore((s) => s.profile);
  const travaux = useAidsStore((s) => s.travaux);
  const location = useAidsStore((s) => s.location);
  const rfr = useAidsStore((s) => s.rfr);
  const householdSize = useAidsStore((s) => s.householdSize);
  const leadId = useAidsStore((s) => s.leadId);
  const reset = useAidsStore((s) => s.reset);
  const setStep = useAidsStore((s) => s.setStep);

  const anahZone = location?.anahZone ?? 'B2C';

  const results = useMemo(
    () =>
      computeAids(
        {
          ...useAidsStore.getState(),
          profile,
          travaux,
          location,
          rfr,
          householdSize,
        },
        anahZone,
      ),
    [profile, travaux, location, rfr, householdSize, anahZone],
  );

  const profileLabel =
    profile === 'occupant'
      ? 'Propriétaire occupant'
      : profile === 'bailleur'
        ? 'Propriétaire bailleur'
        : profile === 'copropriete'
          ? 'Copropriété'
          : 'Locataire';

  const rows: Array<{
    label: string;
    min: number;
    max: number;
    color: string;
    icon: LucideIcon;
    note: string;
    badge?: string;
  }> = [
    {
      label: results.mprLabel,
      min: results.mprMin,
      max: results.mprMax,
      color: 'bg-brand-500',
      icon: Home,
      note:
        profile === 'copropriete'
          ? 'Forfait collectif'
          : profile === 'bailleur'
            ? 'Plafond 20 000 €'
            : results.bracket
              ? 'Subvention non remboursable'
              : '',
    },
    {
      label:
        profile === 'copropriete'
          ? 'CEE collectifs bonifiés'
          : 'Primes CEE',
      min: results.ceeMin,
      max: results.ceeMax,
      color: 'bg-accent-500',
      icon: TrendingUp,
      note: "Cumulable MaPrimeRénov'",
      badge: results.coupDePouceMax > 0
        ? `dont ${fmtEuro(results.coupDePouceMin)}–${fmtEuro(results.coupDePouceMax)} € Coup de Pouce`
        : undefined,
    },
    {
      label: 'TVA 5,5 %',
      min: results.tvaEco,
      max: results.tvaEcoMax,
      color: 'bg-lavender-500',
      icon: Euro,
      note: 'Économie vs 20 %',
    },
    {
      label: 'Éco-PTZ (prêt 0 %)',
      min: 0,
      max: results.ecoPtzMax,
      color: 'bg-stone-700',
      icon: Wrench,
      note: 'Prêt sans intérêts',
    },
  ];

  const noResult = results.totalMax === 0;
  const selectedTravaux = travaux
    .map((id) => findTravaux(id))
    .filter((t): t is NonNullable<ReturnType<typeof findTravaux>> => !!t);

  const allTags = Array.from(
    new Set(selectedTravaux.flatMap((t) => t.tags)),
  ) as TravauxTag[];

  const tauxMax = Math.min(100, results.tauxAideMax);

  return (
    <div>
      {/* ── Confirmation lead + headline ──────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: easeStripe }}
      >
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-brand-700 ring-4 ring-brand-50">
            <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
          </span>
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-700">
            {leadId ? 'Estimation prête · dossier transmis' : 'Estimation prête'}
          </p>
        </div>

        <h2 className="mt-3 h-section">
          Vos aides cumulées
        </h2>

        <div className="mt-3 flex items-end gap-2">
          <p className="font-display text-3xl font-black tabular-nums leading-tight text-ink [overflow-wrap:anywhere] sm:text-4xl md:text-5xl">
            {fmtEuro(results.totalMin)}{' '}
            <span className="text-stone-300">–</span>{' '}
            {fmtEuro(results.totalMax)}{' '}
            <span className="text-xl font-bold text-stone-400 sm:text-2xl md:text-3xl">€</span>
          </p>
        </div>

        {tauxMax > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-stone-100">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${tauxMax}%` }}
                transition={{ duration: 0.9, ease: easeStripe, delay: 0.2 }}
                className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-500"
              />
            </div>
            <span className="shrink-0 text-[12px] font-bold tabular-nums text-brand-700">
              jusqu'à {tauxMax} % couvert
            </span>
          </div>
        )}

        <p className="mt-2 text-[13px] text-stone-500">
          Pour {profileLabel.toLowerCase()} ·{' '}
          {selectedTravaux.length}{' '}
          {selectedTravaux.length === 1 ? 'poste de travaux' : 'postes de travaux'}{' '}
          · estimation Solution Éco France
        </p>
      </motion.div>

      {/* ── Détail par dispositif ─────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4, ease: easeStripe }}
        className="mt-7 rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
          Détail par dispositif
        </p>
        <div className="mt-3 space-y-3">
          {rows.map((row, idx) => {
            if (row.max === 0) return null;
            const Icon = row.icon;
            const pct = Math.min(
              100,
              (row.max / (results.totalMax || 1)) * 100,
            );
            return (
              <motion.div
                key={row.label}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: 0.15 + idx * 0.06,
                  duration: 0.3,
                  ease: easeStripe,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                          row.color,
                        )}
                      >
                        <Icon className="h-3 w-3 text-white" strokeWidth={2.2} />
                      </div>
                      <p className="truncate text-[13px] font-semibold text-stone-800">
                        {row.label}
                      </p>
                    </div>
                    {row.badge && (
                      <span className="ml-8 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 sm:ml-0">
                        <Sparkles className="h-2.5 w-2.5" strokeWidth={2.4} />
                        {row.badge}
                      </span>
                    )}
                    {row.note && (
                      <span className="ml-8 hidden text-[11px] text-stone-400 sm:ml-0 sm:inline">
                        {row.note}
                      </span>
                    )}
                  </div>
                  <p className="shrink-0 text-[13px] font-bold tabular-nums text-stone-900">
                    {row.min === row.max
                      ? `${fmtEuro(row.max)} €`
                      : `${fmtEuro(row.min)}–${fmtEuro(row.max)} €`}
                  </p>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-stone-100">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{
                      duration: 0.7,
                      delay: 0.25 + idx * 0.06,
                      ease: easeStripe,
                    }}
                    className={cn('h-full rounded-full', row.color)}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Tags cumulés */}
        {allTags.length > 0 && (
          <div className="mt-5 border-t border-stone-100 pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
              Dispositifs cumulés
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {allTags.map((tag) => {
                const meta = TRAVAUX_TAG_META[tag];
                return (
                  <span
                    key={tag}
                    title={meta.full}
                    className={cn(
                      'inline-flex items-center rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ring-1 ring-inset',
                      TAG_TONE_CLASSES[meta.tone],
                    )}
                  >
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Reste à charge ─────────────────────────────────── */}
      {results.resteAChargeMax > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.35, ease: easeStripe }}
          className="mt-4 rounded-2xl border border-stone-200 bg-white p-4 shadow-[0_1px_8px_-4px_rgba(0,0,0,.06)]"
        >
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-100">
              <Wallet className="h-4 w-4 text-stone-600" strokeWidth={1.7} />
            </span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-stone-400">
                Reste à charge estimé
              </p>
              <p className="font-display text-xl font-black tabular-nums text-ink">
                {fmtEuro(results.resteAChargeMin)}
                {results.resteAChargeMin !== results.resteAChargeMax && (
                  <>
                    {' '}–{' '}
                    {fmtEuro(results.resteAChargeMax)}
                  </>
                )}{' '}
                <span className="text-base font-bold text-stone-400">€</span>
              </p>
            </div>
          </div>

          {/* Décomposition coût → aides → reste */}
          <div className="mt-3 flex items-center gap-1 text-[11.5px]">
            <span className="rounded bg-stone-100 px-1.5 py-0.5 font-semibold text-stone-700">
              {fmtEuro(results.totalCostMin)}–{fmtEuro(results.totalCostMax)} €
            </span>
            <span className="text-stone-400">travaux</span>
            <span className="text-stone-400">−</span>
            <span className="rounded bg-brand-100 px-1.5 py-0.5 font-semibold text-brand-700">
              {fmtEuro(results.totalMin)}–{fmtEuro(results.totalMax)} €
            </span>
            <span className="text-stone-400">aides</span>
            <span className="text-stone-400">=</span>
            <span className="font-bold text-stone-800">
              votre part
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-stone-400">
            Hors Éco-PTZ (prêt remboursable sans intérêts — {fmtEuro(results.ecoPtzMax)} € max).
          </p>
        </motion.div>
      )}

      {/* ── Progression DPE ────────────────────────────────── */}
      {results.dpeActuel && results.dpeTarget && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.35, ease: easeStripe }}
          className="mt-4 rounded-2xl border border-stone-200 bg-white p-4 shadow-[0_1px_8px_-4px_rgba(0,0,0,.06)]"
        >
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-50">
              <Zap className="h-4 w-4 text-brand-600" strokeWidth={1.7} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-stone-400">
              Progression DPE estimée
            </p>
          </div>
          <DpeProgressBar from={results.dpeActuel} to={results.dpeTarget} />
          {results.dpeActuel === results.dpeTarget ? (
            <p className="mt-2 text-[11.5px] text-stone-500">
              Les travaux sélectionnés améliorent la performance mais ne changent pas encore la classe DPE.
              Combinez plusieurs gestes pour changer de catégorie.
            </p>
          ) : (
            <p className="mt-2 text-[11.5px] text-stone-500">
              Après travaux, votre logement passerait de{' '}
              <strong className="text-ink">classe {results.dpeActuel}</strong> à{' '}
              <strong className="text-ink">classe {results.dpeTarget}</strong> — estimation indicative.
            </p>
          )}
        </motion.div>
      )}

      {/* ── Économie annuelle + ROI ────────────────────────── */}
      {results.economieAnnuelleMax !== null && results.economieAnnuelleMax > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.35, ease: easeStripe }}
          className="mt-4 rounded-2xl border border-accent-200 bg-accent-50/40 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-accent-100">
              <TrendingUp className="h-4 w-4 text-accent-700" strokeWidth={1.7} />
            </span>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-stone-400">
                Économie sur votre facture énergie
              </p>
              <p className="mt-0.5 font-display text-xl font-black tabular-nums text-ink">
                {fmtEuro(results.economieAnnuelleMin ?? 0)}
                {results.economieAnnuelleMin !== results.economieAnnuelleMax && (
                  <>
                    {' '}–{' '}
                    {fmtEuro(results.economieAnnuelleMax ?? 0)}
                  </>
                )}{' '}
                <span className="text-base font-bold text-stone-400">€/an</span>
              </p>
            </div>
            {results.roiMonthsMin !== null && results.roiMonthsMax !== null && (
              <div className="flex-shrink-0 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-stone-400">
                  Retour invest.
                </p>
                <div className="mt-0.5 flex items-center gap-1">
                  <Timer className="h-3.5 w-3.5 text-accent-600" strokeWidth={1.7} />
                  <p className="text-[13px] font-bold text-accent-800">
                    {results.roiMonthsMin < 12
                      ? `${results.roiMonthsMin}–${results.roiMonthsMax} mois`
                      : `${Math.round(results.roiMonthsMin / 12)}–${Math.round(results.roiMonthsMax / 12)} ans`}
                  </p>
                </div>
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-stone-500">
            Basé sur votre facture déclarée et les moyennes ADEME. Valeur indicative.
          </p>
        </motion.div>
      )}

      {/* ── Coup de Pouce spotlight ───────────────────────── */}
      {results.coupDePouceMax > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.35, ease: easeStripe }}
          className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-100">
              <Sparkles className="h-4 w-4 text-amber-700" strokeWidth={1.7} />
            </span>
            <div>
              <p className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-900">
                <BadgeCheck className="h-4 w-4 text-amber-600" strokeWidth={2} />
                Bonus CEE Coup de Pouce inclus
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-amber-800">
                Vos travaux incluent des équipements bénéficiant du CEE bonifié Coup de Pouce
                ({fmtEuro(results.coupDePouceMin)}–{fmtEuro(results.coupDePouceMax)} €
                de majoration inclus dans la prime CEE ci-dessus). Ce bonus est conditionné au
                remplacement d'un équipement fossile existant.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Warnings ──────────────────────────────────────── */}
      {results.warnings.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
          <ul className="space-y-1.5">
            {results.warnings.map((w) => (
              <li
                key={w}
                className="flex items-start gap-2 text-[12px] text-amber-900"
              >
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Source / disclaimer ───────────────────────────── */}
      <div className="mt-5 flex items-start gap-2 rounded-xl border border-stone-200 bg-stone-50 p-3">
        <HelpCircle
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"
          strokeWidth={1.8}
        />
        <p className="text-[11.5px] leading-relaxed text-stone-500">
          Estimation indicative basée sur les barèmes officiels 2026. Le montant
          final dépendra du devis certifié d'un artisan RGE et de la validation
          Anah (ou Anah Copro). L'éco-PTZ est un prêt à taux zéro, non une
          subvention. La progression DPE est approximative.
        </p>
      </div>

      {/* ── Récap + prochaines étapes ─────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.4, ease: easeStripe }}
        className="mt-6 grid gap-3 sm:grid-cols-2"
      >
        <NextStepCard
          icon={Receipt}
          title="Vérification du dossier"
          desc="Un conseiller valide votre éligibilité aux aides détectées (pièces, plafonds, calendrier)."
        />
        <NextStepCard
          icon={Phone}
          title="Recontact sous 24 h ouvrées"
          desc="Au numéro renseigné, sans démarchage commercial."
        />
      </motion.div>

      {/* ── CTA ───────────────────────────────────────────── */}
      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <Button asChild variant="brand" size="xl" className="flex-1 gap-2">
          <a href="/contact?source=calculer-mes-aides">
            <Phone className="h-4 w-4" />
            Parler à un conseiller maintenant
          </a>
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="xl"
          onClick={() => setStep(3, -1)}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Modifier mes travaux
        </Button>
      </div>

      <button
        type="button"
        onClick={reset}
        className="mt-3 w-full text-center text-[12px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline"
      >
        Recommencer un nouveau calcul
      </button>

      {noResult && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4">
          <HandCoins
            className="mt-0.5 h-5 w-5 shrink-0 text-stone-400"
            strokeWidth={1.7}
          />
          <p className="text-[12.5px] text-stone-600">
            Avec votre profil et les travaux indiqués, le calcul n'a pas
            identifié d'aide cumulable. Un conseiller peut analyser des
            dispositifs plus spécifiques (locales, sectorielles).{' '}
            <a
              href="/contact?source=calculer-mes-aides"
              className="font-semibold text-brand-700 hover:underline"
            >
              Demander une étude personnalisée →
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Barre de progression DPE ──────────────────────────── */

const DPE_CLASSES = ['G', 'F', 'E', 'D', 'C', 'B', 'A'];

function DpeProgressBar({ from, to }: { from: string; to: string }) {
  const fromIdx = DPE_CLASSES.indexOf(from);
  const toIdx = DPE_CLASSES.indexOf(to);

  return (
    <div className="flex items-center gap-1">
      {DPE_CLASSES.map((cls, i) => {
        const isFrom = cls === from;
        const isTo = cls === to;
        const inRange = i >= fromIdx && i <= toIdx;

        return (
          <div key={cls} className="flex flex-col items-center gap-0.5">
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-md text-[12px] font-black transition-all',
                isTo
                  ? DPE_COLORS[cls]
                  : inRange
                    ? `${DPE_COLORS[cls]} opacity-50`
                    : isFrom
                      ? cn(DPE_COLORS[cls], 'opacity-70 ring-2 ring-offset-1', DPE_BORDER[cls])
                      : 'bg-stone-100 text-stone-300',
              )}
            >
              {cls}
            </div>
            {isFrom && (
              <p className="text-[8px] font-bold uppercase text-stone-400">Actuel</p>
            )}
            {isTo && from !== to && (
              <p className="text-[8px] font-bold uppercase text-brand-600">Cible</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Carte étape suivante ──────────────────────────────── */

function NextStepCard({
  icon: Icon,
  title,
  desc,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <Icon className="h-4 w-4" strokeWidth={1.7} />
      </span>
      <p className="mt-2.5 text-[13.5px] font-semibold text-ink">{title}</p>
      <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
        {desc}
      </p>
    </div>
  );
}
